import { utils } from 'web3';
import BigNumber from 'bignumber.js';

const ETHER = 'ether';

export const ethToWei = (aAmount: string): string => {
  return utils.toWei(aAmount, ETHER);
};

export const weiToEth = (aAmount: string): string => {
  return utils.fromWei(aAmount, ETHER);
};

/**
 * 숫자에 백단위마다 콤마 추가
 * @param num - 콤마를 추가할 숫자 문자열
 * @returns 콤마가 추가된 문자열
 */
export const addCommas = (num: string): string => {
  // 소수점이 있는 경우 정수부와 소수부 분리
  const parts = num.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // 정수부에 콤마 추가 (3자리마다)
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // 소수부가 있으면 소수점과 함께 반환, 없으면 정수부만 반환
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * wei 단위의 BigInt 값을 ether로 변환하고 소수점 6자리까지 절사한 후 콤마 추가
 * @param weiAmount - wei 단위의 BigInt 값
 * @param decimals - 토큰의 소수점 자릿수 (기본값: 18)
 * @returns 소수점 6자리까지 절사되고 콤마가 추가된 ether 단위 문자열
 */
export const truncateWeiTo6Decimals = (weiAmount: bigint | string, decimals: number = 18): string => {
  // BigInt를 문자열로 변환
  const weiString = weiAmount.toString();

  // wei를 ether로 변환 (소수점 자릿수 고려)
  const etherAmount = utils.fromWei(weiString, decimals);

  // BigNumber로 변환하여 정밀도 보장
  const bnAmount = new BigNumber(etherAmount);

  // 소수점 6자리까지 절사 (절댓값 보존)
  const truncatedEther = bnAmount.decimalPlaces(6, bnAmount.isPositive() ? BigNumber.ROUND_DOWN : BigNumber.ROUND_UP);

  // 콤마 추가하여 반환
  return addCommas(truncatedEther.toString());
};

/**
 * ether 단위의 amount를 소수점 6자리까지 절사 (값 보존)
 * @param amount - 절사할 금액 (ether 단위)
 * @returns 소수점 6자리까지 절사된 금액 (ether 단위)
 */
export const truncateEtherTo6Decimals = (amount: string): string => {
  // BigNumber로 변환하여 정밀도 보장
  const bnAmount = new BigNumber(amount);

  // 소수점 6자리까지 절사 (절댓값 보존)
  const truncatedEther = bnAmount.decimalPlaces(6, bnAmount.isPositive() ? BigNumber.ROUND_DOWN : BigNumber.ROUND_UP);

  return truncatedEther.toString();
};

/**
 * wei 단위의 amount를 소수점 6자리까지 절사 (값 보존)
 * @param amount - 절사할 금액 (wei 단위)
 * @returns 소수점 6자리까지 절사된 금액 (wei 단위)
 */
export const truncateTo6DecimalsPrecise = (amount: string, decimals: number = 6): string => {
  // wei를 ether로 변환
  const etherAmount = utils.fromWei(amount, ETHER);

  // BigNumber로 변환하여 정밀도 설정
  const bnAmount = new BigNumber(etherAmount);

  // 소수점 6자리까지 절사 (절댓값 보존)
  const truncatedEther = bnAmount.decimalPlaces(
    decimals,
    bnAmount.isPositive() ? BigNumber.ROUND_DOWN : BigNumber.ROUND_UP,
  );

  // 다시 wei로 변환하여 반환
  return utils.toWei(truncatedEther.toString(), ETHER);
};
